var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { flatMap } from 'lodash';
import Box from '../../Box';
import Icon from '../../Icon';
import IconButton from '../../IconButton';
import { ExpandLess, ExpandMore, Delete, Edit } from '../../Icons/Core';
import { MenuItem } from '../../Menu';
import { colors, useTheme } from '../../styles';
export var AccountSwitcherStates;
(function (AccountSwitcherStates) {
    AccountSwitcherStates[AccountSwitcherStates["minimized"] = 0] = "minimized";
    AccountSwitcherStates[AccountSwitcherStates["opened"] = 1] = "opened";
})(AccountSwitcherStates || (AccountSwitcherStates = {}));
export const useAccountSwitcherLogic = ({ accounts, initAccount, onSelectAccountCallback, onDelete, onRename }) => {
    const [accountSwitcherState, setAccountSwitcherState] = useState(AccountSwitcherStates.minimized);
    const [search, setSearch] = useState('');
    const [collapsedSubheaders, setCollapsedSubheaders] = useState({});
    const [actionView, setActionView] = useState('none');
    const [selectedWorkspace, setSelectedWorkspace] = useState();
    const [actionTitle, setActionTitle] = useState('');
    const [actionButtonText, setActionButtonText] = useState('');
    const [successText, setSuccessText] = useState('');
    const [confirmationText, setConfirmationText] = useState('');
    const theme = useTheme();
    const toggleCollapse = useCallback((accountId) => {
        setCollapsedSubheaders(prevState => {
            return Object.assign(Object.assign({}, prevState), { [accountId]: !prevState[accountId] });
        });
    }, []);
    const flattenedAccountsWithParentIds = useMemo(() => {
        return flatMap(accounts, account => {
            if (account.nestedAccounts && account.nestedAccounts.length > 0) {
                return account.nestedAccounts.map(nestedAccount => ({
                    id: nestedAccount.id,
                    name: nestedAccount.name,
                    parentId: account.id,
                    parentName: account.name
                }));
            }
            return [{ id: account.id, name: account.name }];
        });
    }, [accounts]);
    // Find active account and its children
    const activeAccount = useMemo(() => flattenedAccountsWithParentIds.find(account => account.id === initAccount), [flattenedAccountsWithParentIds, initAccount]);
    const filteredAccounts = useMemo(() => {
        return accounts.filter(account => {
            if (account.nestedAccounts && account.nestedAccounts.length > 0) {
                return (account.nestedAccounts || []).some(nestedAccount => nestedAccount.name.toLowerCase().includes(search.toLowerCase()));
            }
            return account.name.toLowerCase().includes(search.toLowerCase());
        });
    }, [accounts, search]);
    const onSelectNewAccount = useCallback((selectedAccountId, nested = false) => {
        let selectedAccount;
        if (nested) {
            selectedAccount = accounts
                .flatMap(account => account.nestedAccounts || [])
                .find(nestedAccount3 => nestedAccount3.id === selectedAccountId);
        }
        else {
            selectedAccount = accounts === null || accounts === void 0 ? void 0 : accounts.find(account => account.id === selectedAccountId);
        }
        if (selectedAccount && (activeAccount === null || activeAccount === void 0 ? void 0 : activeAccount.id) !== selectedAccount.id) {
            setAccountSwitcherState(AccountSwitcherStates.minimized);
            onSelectAccountCallback(selectedAccount);
        }
    }, [accounts, activeAccount, onSelectAccountCallback]);
    const onToggleSwitcherState = useCallback(() => {
        setSearch('');
        setAccountSwitcherState(accountSwitcherState === AccountSwitcherStates.minimized
            ? AccountSwitcherStates.opened
            : AccountSwitcherStates.minimized);
    }, [accountSwitcherState]);
    const onSearch = useCallback((e) => {
        setSearch(e.target.value);
    }, []);
    const onTriggerRename = useCallback((workspace) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setSelectedWorkspace(workspace);
        setActionView('rename');
        const result = yield (onRename === null || onRename === void 0 ? void 0 : onRename(workspace));
        setActionTitle((_a = result === null || result === void 0 ? void 0 : result.title) !== null && _a !== void 0 ? _a : 'Rename Workspace');
        setActionButtonText((_b = result === null || result === void 0 ? void 0 : result.buttonText) !== null && _b !== void 0 ? _b : 'Save');
    }), [onRename]);
    const onTriggerDelete = useCallback((workspace) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e;
        setSelectedWorkspace(workspace);
        setActionView('delete');
        const result = yield (onDelete === null || onDelete === void 0 ? void 0 : onDelete(workspace));
        setActionTitle((_c = result === null || result === void 0 ? void 0 : result.title) !== null && _c !== void 0 ? _c : 'Delete Workspace');
        setActionButtonText((_d = result === null || result === void 0 ? void 0 : result.buttonText) !== null && _d !== void 0 ? _d : 'Delete');
        setConfirmationText((result === null || result === void 0 ? void 0 : result.confirmationText)
            ? result.confirmationText
                .replace('{workspaceName}', workspace.name)
                .replace('{parentName}', (_e = activeAccount === null || activeAccount === void 0 ? void 0 : activeAccount.name) !== null && _e !== void 0 ? _e : '')
            : `Are you sure you want to permanently delete ${workspace.name}${(activeAccount === null || activeAccount === void 0 ? void 0 : activeAccount.name) ? ` from ${activeAccount.name}` : ''}?`);
    }), [onDelete, activeAccount]);
    const onResetAction = useCallback(() => {
        setSelectedWorkspace(undefined);
        setActionView('none');
        setActionTitle('');
        setActionButtonText('');
        setSuccessText('');
        setConfirmationText('');
    }, []);
    const onConfirmAction = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedWorkspace)
            return;
        const handler = actionView === 'rename' ? onRename : onDelete;
        if (!handler)
            return;
        const result = yield handler(selectedWorkspace);
        if (result === null || result === void 0 ? void 0 : result.successText) {
            setSuccessText(result.successText);
            setActionView('none');
            setTimeout(() => {
                setSuccessText('');
            }, 3000);
        }
        else {
            onResetAction();
        }
    }), [selectedWorkspace, actionView, onRename, onDelete, onResetAction]);
    const onUpdateRenameField = useCallback((name) => {
        if (selectedWorkspace) {
            setSelectedWorkspace(Object.assign(Object.assign({}, selectedWorkspace), { name }));
        }
    }, [selectedWorkspace]);
    const onSetActionTitle = useCallback((title) => {
        setActionTitle(title);
    }, []);
    const hasNoResults = useMemo(() => {
        return search !== '' && filteredAccounts.length === 0;
    }, [search, filteredAccounts]);
    const handleExpandClick = useCallback((e, accountId) => {
        e.stopPropagation();
        toggleCollapse(accountId);
    }, [toggleCollapse]);
    const onSetActionButtonText = useCallback((text) => setActionButtonText(text), []);
    const onSetConfirmationText = useCallback((text) => setConfirmationText(text), []);
    const onSetSuccessText = useCallback((text) => setSuccessText(text), []);
    const onSetWorkspace = useCallback((workspace) => setSelectedWorkspace(workspace), []);
    const filteredAccountsList = useMemo(() => {
        return filteredAccounts
            .filter(account => account.id !== (activeAccount === null || activeAccount === void 0 ? void 0 : activeAccount.id)) // Filter out the active account
            .sort((a, b) => a.name.localeCompare(b.name)) // Sort accounts alphabetically
            .map((account) => {
            if (account.nestedAccounts && account.nestedAccounts.length > 0) {
                const nestedAccountItems = account.nestedAccounts
                    .filter(nestedAccount => nestedAccount.id !== (activeAccount === null || activeAccount === void 0 ? void 0 : activeAccount.id)) // Filter out the active account in nested accounts
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(nestedAccount => (React.createElement(MenuItem, { key: nestedAccount.id, onClick: () => onSelectNewAccount(nestedAccount.id, true), sx: { paddingLeft: '32px' } },
                    React.createElement(Highlighter, { searchWords: [search], textToHighlight: nestedAccount.name, autoEscape: true, highlightStyle: { backgroundColor: colors.highlightYellow } }),
                    (onRename || onDelete) && (React.createElement(Box, { sx: {
                            display: 'flex',
                            gap: theme.palette.space.component.default,
                            justifyContent: 'flex-end',
                            flexGrow: 1
                        } },
                        onRename && (React.createElement(IconButton, { bdsSize: "medium", onClick: e => {
                                e.stopPropagation();
                                onTriggerRename(nestedAccount);
                            } },
                            React.createElement(Edit, null))),
                        onDelete && (React.createElement(IconButton, { bdsSize: "medium", onClick: e => {
                                e.stopPropagation();
                                onTriggerDelete(nestedAccount);
                            } },
                            React.createElement(Delete, null))))))))
                    .filter(item => item !== null); // Filter out null values
                if (nestedAccountItems.length > 0) {
                    return [
                        React.createElement(MenuItem, { key: `${account.id}-header`, disableGutters: true, onClick: () => toggleCollapse(account.id) },
                            React.createElement(Box, { sx: { paddingRight: theme.palette.space.container.dense } }, collapsedSubheaders[account.id] !== true ? (React.createElement(Icon, { bdsSize: "small", bdsType: "staticEmphasis", bdsIcon: ExpandLess, onClick: (e) => handleExpandClick(e, account.id) })) : (React.createElement(Icon, { bdsSize: "small", bdsType: "staticEmphasis", bdsIcon: ExpandMore, onClick: (e) => handleExpandClick(e, account.id) }))),
                            account.name),
                        ...(collapsedSubheaders[account.id] !== true ? nestedAccountItems : [])
                    ];
                }
            }
            if (!account.nestedAccounts || account.nestedAccounts.length === 0) {
                return (React.createElement(MenuItem, { key: account.id, onClick: () => onSelectNewAccount(account.id, false) },
                    React.createElement(Highlighter, { searchWords: [search], textToHighlight: account.name, autoEscape: true, highlightStyle: { backgroundColor: colors.highlightYellow } })));
            }
            return null;
        })
            .filter(item => item !== null);
    }, [
        filteredAccounts,
        search,
        onSelectNewAccount,
        collapsedSubheaders,
        toggleCollapse,
        handleExpandClick,
        activeAccount
    ]);
    return useMemo(() => [
        {
            accountSwitcherState,
            actionButtonText,
            actionTitle,
            actionView,
            activeAccount,
            confirmationText,
            hasNoResults,
            search,
            selectedWorkspace,
            successText
        },
        {
            onSelectNewAccount,
            onToggleSwitcherState,
            onSearch,
            onTriggerRename,
            onTriggerDelete,
            onUpdateRenameField,
            onResetAction,
            onSetActionTitle,
            onSetActionButtonText,
            onSetConfirmationText,
            onSetSuccessText,
            onSetWorkspace,
            onConfirmAction
        },
        filteredAccountsList
    ], [
        activeAccount,
        accountSwitcherState,
        search,
        actionView,
        selectedWorkspace,
        actionTitle,
        actionButtonText,
        confirmationText,
        successText,
        onSelectNewAccount,
        onToggleSwitcherState,
        onSearch,
        onTriggerRename,
        onTriggerDelete,
        onUpdateRenameField,
        onResetAction,
        onSetActionTitle,
        onSetActionButtonText,
        onSetConfirmationText,
        onSetSuccessText,
        onSetWorkspace,
        onConfirmAction,
        filteredAccountsList
    ]);
};
//# sourceMappingURL=useAccountSwitcherLogic.js.map